import type {SourceInfo} from '@github-ui/custom-properties-types'
import {useMutation} from '@github-ui/react-query'
import sudo from '@github-ui/sudo'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useState} from 'react'

import {ServerErrorFormBanner} from './ServerErrorFormBanner'

interface Props {
  business: SourceInfo
  businessPath: string
  promotePath: string
  returnFocusRef?: RefObject<HTMLElement>
  onSuccess: () => void
  onCancel: () => void
}
export function PromoteDefinitionDialog({
  business,
  promotePath,
  businessPath,
  returnFocusRef,
  onSuccess,
  onCancel,
}: Props) {
  const [promoting, setPromoting] = useState(false)
  const [serverErrorMessage, setServerErrorMessage] = useState('')

  const promoteMutation = useMutation({
    mutationFn: promoteDefinition,
    onMutate: async () => {
      setPromoting(true)
      setServerErrorMessage('')

      if (!(await sudo())) throw new Error('Unauthorized')
    },
    onSuccess,
    onError: error => {
      setServerErrorMessage(error.message)
      setPromoting(false)
    },
  })

  // Escape focus trap when sudo dialog is open
  if (promoting) return null

  return (
    <Dialog
      width="large"
      data-testid="promote-definition-dialog"
      onClose={onCancel}
      title="Promote to enterprise"
      returnFocusRef={returnFocusRef}
      renderBody={() => (
        <>
          <div aria-live="polite">
            {serverErrorMessage && (
              <div className="pl-3 pr-3 pt-2">
                <ServerErrorFormBanner>{serverErrorMessage}</ServerErrorFormBanner>
              </div>
            )}
          </div>

          <div className="p-3">
            <p>
              Promote this property to the enterprise level and make it available to every organization in your
              enterprise.
            </p>

            <p className="text-bold">What happens when you promote a property:</p>
            <ul className="ml-4">
              <li>
                Ownership of the property moves from the organization to the enterprise (
                <a href={businessPath}>{business.name}</a>)
              </li>
              <li>All organizations in the enterprise will have access to the property</li>
              <li>All references to the property in the organization will still work.</li>
            </ul>
          </div>
        </>
      )}
      footerButtons={[
        {
          onClick: onCancel,
          content: 'Cancel',
        },
        {
          buttonType: 'primary',
          onClick: () => promoteMutation.mutate(promotePath),
          content: promoting ? 'Promoting…' : 'Promote',
          'aria-disabled': promoting,
        },
      ]}
    />
  )
}

const promoteDefinition = async (path: string) => {
  const result = await reactFetchJSON(path, {method: 'POST'})
  if (result.ok) return

  throw new Error(`Something went wrong.`)
}

try{ PromoteDefinitionDialog.displayName ||= 'PromoteDefinitionDialog' } catch {}